/**
 * Audit log — `/admin/audit-log`.
 *
 * What the log is determines how it is drawn. It is append-only and
 * company-wide: there is nothing to edit here and no branch filter to offer,
 * because a branch cannot audit itself. Events arrive newest first.
 *
 * `before` and `after` carry the CHANGED fields only, never a whole row, and
 * the acting branch is nullable — company-wide and system work legitimately
 * has none, so an empty cell means "no branch", not "unknown".
 *
 * The row is rendered as the sentence it is: who did what to which record,
 * with the two sides of every changed field underneath.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { formatDateTime } from "@/lib/format";

import { listAuditLog, type AuditEvent, type AuditFilters } from "../api-audit";

const ALL = "__all__";
const PAGE = 100;

/** The subjects the log actually records, so the filter offers what exists
 *  rather than a free-text box that mostly finds nothing. */
const ENTITY_TYPES = [
  "gl_account",
  "party",
  "catalog_product",
  "product_variant",
  "catalog_uom",
  "inventory_stock_movement",
  "inventory_transfer",
  "purchasing_purchase_order",
  "purchasing_supplier_item",
  "finance_customer_invoice",
  "finance_tax_code",
  "user",
  "approval_request",
] as const;

export interface AuditLogPageProps {
  pageLimit?: number;
}

export function AuditLogPage({ pageLimit = PAGE }: AuditLogPageProps) {
  const { t, i18n } = useTranslation();
  const [entityType, setEntityType] = useState<string>(ALL);
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const filters = useMemo<AuditFilters>(() => {
    const built: AuditFilters = {};
    if (entityType !== ALL) built.entity_type = entityType;
    if (action.trim() !== "") built.action = action.trim();
    if (from !== "") built.occurred_from = new Date(from).toISOString();
    if (to !== "") built.occurred_to = new Date(to).toISOString();
    return built;
  }, [entityType, action, from, to]);

  const events = useQuery<AuditEvent[]>({
    queryKey: ["platform/audit-log", { ...filters, limit: pageLimit }],
    queryFn: async ({ signal }) => [
      ...(await listAuditLog(filters, { limit: pageLimit, signal })).items,
    ],
  });

  const when = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const rows = events.data ?? [];

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">{t("audit.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "audit.subtitle",
            "Every change the system recorded, newest first. The log is append-only: nothing here can be edited or removed.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="audit-entity">{t("audit.entity")}</Label>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger id="audit-entity" className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {ENTITY_TYPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`audit.entityType.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="audit-action">{t("audit.action")}</Label>
          <Input
            id="audit-action"
            dir="ltr"
            className="w-64 font-mono"
            placeholder="purchasing.purchase_order.submitted"
            value={action}
            onChange={(event) => setAction(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="audit-from">{t("audit.from", "From")}</Label>
          <Input
            id="audit-from"
            type="date"
            className="w-40"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="audit-to">{t("audit.to", "To")}</Label>
          <Input
            id="audit-to"
            type="date"
            className="w-40"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
      </div>

      {events.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
      ) : events.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {t("audit.error", "The audit log could not be read.")}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("audit.empty", "No recorded change matches these filters.")}
        </p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y">
              {rows.map((event) => (
                <li key={event.id} className="space-y-2 p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="flex flex-wrap items-baseline gap-2">
                      <span dir="ltr" className="font-mono text-sm">
                        {event.action}
                      </span>
                      <Badge variant="outline">
                        {t(`audit.entityType.${event.entity_type}`, event.entity_type)}
                      </Badge>
                    </p>
                    <span className="text-xs text-muted-foreground">{when(event.occurred_at)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {event.actor_id === null
                      ? t("audit.systemActor", "System")
                      : t("audit.byActor", "Actor {{id}}", { id: event.actor_id.slice(0, 8) })}
                    {" · "}
                    {event.acting_branch_id === null
                      ? t("audit.companyWide", "Company-wide")
                      : t("audit.inBranch", "Branch {{id}}", {
                          id: event.acting_branch_id.slice(0, 8),
                        })}
                    {" · "}
                    <span dir="ltr" className="font-mono">
                      {event.entity_id.slice(0, 8)}
                    </span>
                  </p>
                  {event.before === null && event.after === null ? null : (
                    <ChangedFields before={event.before} after={event.after} />
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** The two sides of each changed field, one row per key. A key present on one
 *  side only is exactly what it looks like: set, or cleared. */
function ChangedFields({
  before,
  after,
}: {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}) {
  const { t } = useTranslation();
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].sort();
  if (keys.length === 0) return null;
  const render = (value: unknown): string =>
    value === undefined ? "—" : typeof value === "string" ? value : JSON.stringify(value);
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground">
          <th scope="col" className="w-1/3 py-1 text-start font-normal">
            {t("audit.field", "Field")}
          </th>
          <th scope="col" className="py-1 text-start font-normal">
            {t("audit.before")}
          </th>
          <th scope="col" className="py-1 text-start font-normal">
            {t("audit.after")}
          </th>
        </tr>
      </thead>
      <tbody>
        {keys.map((key) => (
          <tr key={key}>
            <td className="py-1 pe-2 align-top">
              <span dir="ltr" className="font-mono">
                {key}
              </span>
            </td>
            <td className="py-1 pe-2 align-top text-muted-foreground">{render(before?.[key])}</td>
            <td className="py-1 align-top">{render(after?.[key])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
